import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { CreateLoaderTable } from "@/components/Loaders";
import { If } from "@/components/If";
import { useEffect, useState } from "react";
import { DollarSign, Users, TrendingUp, Trophy, FileDown, Search, ChevronUp, ChevronDown, CheckCircle, XCircle, MinusCircle, Save } from "lucide-react";
import {
  getSellerCommissionRules,
  getCommissionsRealTime,
  exportCommissionsExcel,
  exportCommissionsPdf,
  saveCommissionLeftover,
  CommissionLeftoverInfo,
  RealTimeCommissionData,
  RealTimeMetrics,
} from "@/Api/sellerCommission";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Pagination } from "@/components/Pagination";
import { toast } from "sonner";
import { getUnitiesData } from "@/Api/unities";
import { getSellersData } from "@/Api/sellers";
import { formatCurrency } from "@/utils/format-currency";
import { cn } from "@/lib/utils";

interface Unity {
  id: number;
  uuid: string;
  name: string;
}

interface CommissionRule {
  id: number;
  uuid: string;
  name: string;
}

interface Seller {
  id: number;
  uuid: string;
  user: { id: number; name: string };
}

// Formata o valor digitado como moeda brasileira (1.000,00)
const formatCurrencyInput = (value: string): string => {
  const numbers = value.replace(/\D/g, "");
  if (!numbers) return "";
  const amount = parseInt(numbers) / 100;
  return amount.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseCurrencyToNumber = (value: string): number => {
  if (!value) return 0;
  const cleaned = value.replace(/\./g, "").replace(",", ".");
  return parseFloat(cleaned) || 0;
};

const numberToCurrencyInput = (value: number): string =>
  value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDateBR = (date: string): string => {
  if (!date) return "";
  const [year, month, day] = date.split("T")[0].split("-");
  return `${day}/${month}/${year}`;
};

const goalLabels = {
  below_goal: "Não Bateu a Meta",
  at_goal: "Bateu Meta",
  above_goal: "Bateu Super Meta",
};

const goalColors = {
  below_goal: "text-red-500 bg-red-50",
  at_goal: "text-green-500 bg-green-50",
  above_goal: "text-blue-500 bg-blue-50",
};

export const SellerCommissionTrackingPage = () => {
  const [data, setData] = useState<RealTimeCommissionData[]>([]);
  const [metrics, setMetrics] = useState<RealTimeMetrics | null>(null);
  const [state, setState] = useState<"loading" | "production">("loading");
  const [unities, setUnities] = useState<Unity[]>([]);
  const [rules, setRules] = useState<CommissionRule[]>([]);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [sellerSearchOpen, setSellerSearchOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Sobras (desconto da premiação) do período carregado
  const [leftoverInfo, setLeftoverInfo] = useState<CommissionLeftoverInfo | null>(null);
  const [leftoverInput, setLeftoverInput] = useState<string>("");
  const [savingLeftover, setSavingLeftover] = useState(false);
  const [loadedScope, setLoadedScope] = useState<{
    period_start: string;
    period_end: string;
    unity_id: number | null;
  } | null>(null);

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 50;

  // Filtros - inicializa com o mês atual
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const [filterUnityId, setFilterUnityId] = useState<string>("all");
  const [filterRuleId, setFilterRuleId] = useState<string>("all");
  const [filterSellerId, setFilterSellerId] = useState<string>("all");
  const [filterPeriodStart, setFilterPeriodStart] = useState<string>(
    firstDay.toISOString().split("T")[0]
  );
  const [filterPeriodEnd, setFilterPeriodEnd] = useState<string>(
    lastDay.toISOString().split("T")[0]
  );
  const [filterStatus, setFilterStatus] = useState<string>("all");

  // Ordenação
  const [sortConfig, setSortConfig] = useState<{
    column: string;
    direction: "asc" | "desc";
  }>({ column: "commission_value", direction: "desc" });

  const loadData = async (page: number = 1) => {
    setState("loading");
    try {
      const filters: Record<string, string | number> = {
        page,
        per_page: perPage,
      };
      if (filterUnityId && filterUnityId !== "all") filters.unity_id = parseInt(filterUnityId);
      if (filterRuleId && filterRuleId !== "all") filters.rule_id = parseInt(filterRuleId);
      if (filterSellerId && filterSellerId !== "all") filters.seller_id = parseInt(filterSellerId);
      if (filterPeriodStart) filters.period_start = filterPeriodStart;
      if (filterPeriodEnd) filters.period_end = filterPeriodEnd;
      if (filterStatus && filterStatus !== "all") filters.goal_status = filterStatus;

      const { data: response } = await getCommissionsRealTime(filters);
      const result = response?.response;

      setData(result?.data || []);
      setMetrics(result?.metrics || null);
      setLeftoverInfo(result?.leftover || null);
      setLeftoverInput(
        result?.leftover?.total_value ? numberToCurrencyInput(Number(result.leftover.total_value)) : ""
      );
      setLoadedScope({
        period_start: result?.period?.start || filterPeriodStart,
        period_end: result?.period?.end || filterPeriodEnd,
        unity_id: filterUnityId && filterUnityId !== "all" ? parseInt(filterUnityId) : null,
      });
      setCurrentPage(result?.current_page || 1);
      setLastPage(result?.last_page || 1);
      setTotal(result?.total || 0);
    } catch (error) {
      toast.error("Erro ao carregar premiações");
    }
    setState("production");
  };

  const loadUnities = async () => {
    try {
      const { data } = await getUnitiesData(1);
      setUnities(data?.response?.data || []);
    } catch (error) {
      console.error("Erro ao carregar unidades:", error);
    }
  };

  const loadRules = async () => {
    try {
      const { data } = await getSellerCommissionRules(1, { is_active: true });
      setRules(data?.response?.data || []);
    } catch (error) {
      console.error("Erro ao carregar regras:", error);
    }
  };

  const loadSellers = async () => {
    try {
      const { data } = await getSellersData(1);
      setSellers(data?.response?.data || []);
    } catch (error) {
      console.error("Erro ao carregar vendedores:", error);
    }
  };

  useEffect(() => {
    loadUnities();
    loadRules();
    loadSellers();
    loadData();
  }, []);

  const handleFilter = () => {
    loadData(1);
  };

  const handleSaveLeftover = async () => {
    if (!loadedScope) return;

    setSavingLeftover(true);
    try {
      await saveCommissionLeftover({
        period_start: loadedScope.period_start,
        period_end: loadedScope.period_end,
        unity_id: loadedScope.unity_id,
        total_value: parseCurrencyToNumber(leftoverInput),
      });
      toast.success("Sobras salvas e rateadas entre os vendedores");
      await loadData(currentPage);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erro ao salvar sobras");
    }
    setSavingLeftover(false);
  };

  const loadedUnityName = loadedScope?.unity_id
    ? unities.find((u) => u.id === loadedScope.unity_id)?.name
    : "Todas as unidades";

  const handleExportExcel = async () => {
    if (data.length === 0) {
      toast.error("Não há dados para exportar");
      return;
    }

    setExporting(true);
    try {
      const blob = await exportCommissionsExcel({
        data,
        metrics: metrics || {
          total_commission: 0,
          total_sellers: 0,
          avg_commission: 0,
          total_sales: 0,
          top_seller: null,
        },
        period: {
          start: filterPeriodStart,
          end: filterPeriodEnd,
        },
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `premiacoes_${filterPeriodStart}_${filterPeriodEnd}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Excel exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar Excel");
    }
    setExporting(false);
  };

  const handleExportPdf = async () => {
    if (data.length === 0) {
      toast.error("Não há dados para exportar");
      return;
    }

    setExporting(true);
    try {
      const blob = await exportCommissionsPdf({
        data,
        metrics: metrics || {
          total_commission: 0,
          total_sellers: 0,
          avg_commission: 0,
          total_sales: 0,
          top_seller: null,
        },
        period: {
          start: filterPeriodStart,
          end: filterPeriodEnd,
        },
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `premiacoes_${filterPeriodStart}_${filterPeriodEnd}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("PDF exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar PDF");
    }
    setExporting(false);
  };

  const handleSort = (column: string) => {
    const direction =
      sortConfig?.column === column && sortConfig?.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ column, direction });
  };

  const getSortIcon = (column: string) => {
    if (sortConfig?.column !== column) {
      return null;
    }
    return sortConfig?.direction === "asc" ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  // Ordenar dados localmente (a paginação vem do backend)
  const sortedData = [...data].sort((a, b) => {
    const column = sortConfig.column as keyof RealTimeCommissionData;
    const aVal = a[column];
    const bVal = b[column];

    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
    }
    if (typeof aVal === "string" && typeof bVal === "string") {
      return sortConfig.direction === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    return 0;
  });

  return (
    <Main>
      <PageHeader
        icon={DollarSign}
        title="Acompanhamento de Premiações"
        description="Acompanhe o valor de premiação ganho por cada vendedor"
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" disabled={exporting}>
              <FileDown className="w-4 h-4 mr-2" />
              {exporting ? "Exportando..." : "Exportar"}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={handleExportPdf} disabled={exporting}>
              Exportar PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleExportExcel} disabled={exporting}>
              Exportar Excel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeader>

      <div className="flex flex-col gap-6">
        {/* Card de Filtros */}
        <div className="bg-white rounded-lg border shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold">Filtros</h3>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFilterUnityId("all");
                  setFilterRuleId("all");
                  setFilterSellerId("all");
                  setFilterStatus("all");
                  const now = new Date();
                  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                  setFilterPeriodStart(firstDay.toISOString().split("T")[0]);
                  setFilterPeriodEnd(lastDay.toISOString().split("T")[0]);
                }}
                className="gap-2"
              >
                <span className="text-lg">×</span>
                Limpar
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            {/* Unidade */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
              <Select value={filterUnityId} onValueChange={setFilterUnityId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Todas as unidades" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as unidades</SelectItem>
                  {unities.map((u) => (
                    <SelectItem key={u.id} value={u.id.toString()}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Data Inicial */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Inicial</label>
              <Input
                type="date"
                value={filterPeriodStart}
                onChange={(e) => setFilterPeriodStart(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Data Final */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Final</label>
              <Input
                type="date"
                value={filterPeriodEnd}
                onChange={(e) => setFilterPeriodEnd(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Regra */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Regra de Premiação</label>
              <Select value={filterRuleId} onValueChange={setFilterRuleId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Todas as Regras" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Regras</SelectItem>
                  {rules.map((r) => (
                    <SelectItem key={r.id} value={r.id.toString()}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Vendedor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendedor</label>
              <Popover open={sellerSearchOpen} onOpenChange={setSellerSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={sellerSearchOpen}
                    className="w-full justify-between"
                  >
                    {filterSellerId && filterSellerId !== "all"
                      ? sellers.find((s) => s.id.toString() === filterSellerId)?.user?.name
                      : "Todos os Vendedores"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-0">
                  <Command>
                    <CommandInput placeholder="Buscar vendedor..." />
                    <CommandList>
                      <CommandEmpty>Nenhum vendedor encontrado.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="all"
                          onSelect={() => {
                            setFilterSellerId("all");
                            setSellerSearchOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              filterSellerId === "all" ? "opacity-100" : "opacity-0"
                            )}
                          />
                          Todos os Vendedores
                        </CommandItem>
                        {sellers.map((s) => (
                          <CommandItem
                            key={s.id}
                            value={s.user?.name}
                            onSelect={() => {
                              setFilterSellerId(s.id.toString());
                              setSellerSearchOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                filterSellerId === s.id.toString() ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {s.user?.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Todos os Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Status</SelectItem>
                  <SelectItem value="above_goal">
                    <div className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-blue-500" />
                      <span className="text-blue-500">Bateu Super Meta</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="at_goal">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-green-500">Bateu Meta</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="below_goal">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span className="text-red-500">Não Bateu a Meta</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Botão Aplicar Filtros */}
          <div className="flex justify-end mt-6">
            <Button onClick={handleFilter} className="bg-slate-800 hover:bg-slate-900">
              <Search className="w-4 h-4 mr-2" />
              Aplicar Filtros
            </Button>
          </div>
        </div>

        {/* Sobras - desconto da premiação rateado proporcionalmente ao valor vendido */}
        <div className="bg-white rounded-lg border shadow-sm p-6">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <MinusCircle className="w-5 h-5 text-amber-600" />
                <h3 className="text-lg font-semibold">Sobras / Desconto da Premiação</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Informe o valor total das sobras. Ele será descontado de cada vendedor proporcionalmente ao valor vendido.
              </p>
              {loadedScope && (
                <p className="text-xs text-muted-foreground mt-1">
                  Período: <span className="font-medium">{formatDateBR(loadedScope.period_start)} a {formatDateBR(loadedScope.period_end)}</span>
                  {" · "}Unidade: <span className="font-medium">{loadedUnityName}</span>
                  {leftoverInfo?.sales_base ? (
                    <>
                      {" · "}Base do rateio: <span className="font-medium">{formatCurrency(leftoverInfo.sales_base)}</span>
                    </>
                  ) : null}
                </p>
              )}
              {leftoverInfo?.updated_at && (
                <p className="text-xs text-muted-foreground mt-1">
                  Última alteração em {new Date(leftoverInfo.updated_at.replace(" ", "T")).toLocaleString("pt-BR")}
                  {leftoverInfo.updated_by ? ` por ${leftoverInfo.updated_by}` : ""}
                </p>
              )}
            </div>
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor das Sobras</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">R$</span>
                  <Input
                    inputMode="numeric"
                    placeholder="0,00"
                    value={leftoverInput}
                    onChange={(e) => setLeftoverInput(formatCurrencyInput(e.target.value))}
                    className="pl-9 w-48 text-right"
                    disabled={!loadedScope || savingLeftover}
                  />
                </div>
              </div>
              <Button
                onClick={handleSaveLeftover}
                disabled={!loadedScope || savingLeftover || state === "loading"}
                className="bg-slate-800 hover:bg-slate-900"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingLeftover ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </div>

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-blue-500 rounded-full">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-blue-700 font-medium">Total de Premiação</p>
              <p className="text-2xl font-bold text-blue-600 truncate">
                {formatCurrency(metrics?.total_commission || 0)}
              </p>
              <p className="text-xs text-blue-600/70">
                {metrics?.total_leftover
                  ? `Valor final: ${formatCurrency(metrics?.total_final_value || 0)}`
                  : "Valor total no período"}
              </p>
            </div>
          </div>

          <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-200 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-emerald-500 rounded-full">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-emerald-700 font-medium">Total de Vendedores</p>
              <p className="text-2xl font-bold text-emerald-600">
                {metrics?.total_sellers || 0}
              </p>
              <p className="text-xs text-emerald-600/70">Vendedores com premiação</p>
            </div>
          </div>

          <div className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl border border-violet-200 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-violet-500 rounded-full">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-violet-700 font-medium">Média por Vendedor</p>
              <p className="text-2xl font-bold text-violet-600 truncate">
                {formatCurrency(metrics?.avg_commission || 0)}
              </p>
              <p className="text-xs text-violet-600/70">Premiação média no período</p>
            </div>
          </div>

          <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-amber-500 rounded-full">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-amber-700 font-medium">Maior Premiação</p>
              <p className="text-2xl font-bold text-amber-600 truncate">
                {metrics?.top_seller ? formatCurrency(metrics.top_seller.value) : "R$ 0,00"}
              </p>
              <p className="text-sm font-semibold text-amber-800 truncate">
                {metrics?.top_seller?.name || "Nenhum vendedor"}
              </p>
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold">Premiação por Vendedor</h3>
            <span className="text-sm text-muted-foreground">{total} registro(s)</span>
          </div>

          <If condition={state === "loading"}>
            <div className="p-4">
              <CreateLoaderTable cols={9} lines={6} />
            </div>
          </If>

          <If condition={state === "production"}>
            <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort("seller_name")}
                  >
                    <div className="flex items-center gap-1">
                      <Search className="h-3 w-3 text-gray-400" />
                      Vendedor{getSortIcon("seller_name")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort("total_sales")}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <Search className="h-3 w-3 text-gray-400" />
                      Vendido (R$){getSortIcon("total_sales")}
                    </div>
                  </TableHead>
                  <TableHead className="text-center">Faixa Aplicada</TableHead>
                  <TableHead
                    className="text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort("rate_applied")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <Search className="h-3 w-3 text-gray-400" />
                      % Tarifa{getSortIcon("rate_applied")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort("commission_value")}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <Search className="h-3 w-3 text-gray-400" />
                      Premiação (R$){getSortIcon("commission_value")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort("leftover_value")}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Sobras (R$){getSortIcon("leftover_value")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort("final_value")}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Valor Final (R$){getSortIcon("final_value")}
                    </div>
                  </TableHead>
                  <TableHead className="text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      Nenhum cálculo de premiação encontrado
                    </TableCell>
                  </TableRow>
                )}
                {sortedData.map((calc, index) => (
                  <TableRow key={calc.seller_id}>
                    <TableCell className="text-muted-foreground">
                      {(currentPage - 1) * perPage + index + 1}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{calc.seller_name}</span>
                            {/* Ícone de status */}
                            {calc.goal_status === "above_goal" && (
                              <Trophy className="w-4 h-4 text-blue-500" />
                            )}
                            {calc.goal_status === "at_goal" && (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            )}
                            {calc.goal_status === "below_goal" && (
                              <XCircle className="w-4 h-4 text-red-500" />
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">{calc.unity_name}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(calc.total_sales)}</TableCell>
                    <TableCell className="text-center">{calc.tier_number}</TableCell>
                    <TableCell className="text-center">{calc.rate_applied.toFixed(2)}%</TableCell>
                    <TableCell className="text-right font-semibold text-green-600">
                      {formatCurrency(calc.commission_value)}
                    </TableCell>
                    <TableCell className="text-right text-amber-700">
                      {formatCurrency(calc.leftover_value || 0)}
                      {calc.leftover_value ? (
                        <span className="block text-xs text-muted-foreground">
                          {(calc.leftover_share || 0).toFixed(2)}% das vendas
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className={cn("text-right font-bold", (calc.final_value ?? calc.commission_value) < 0 ? "text-red-600" : "text-slate-900")}>
                      {formatCurrency(calc.final_value ?? calc.commission_value)}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={cn("px-3 py-1 rounded-full text-sm font-medium", goalColors[calc.goal_status])}>
                        {goalLabels[calc.goal_status]}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Linha de Total */}
                {sortedData.length > 0 && (
                  <TableRow className="bg-muted/50 font-semibold">
                    <TableCell></TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(metrics?.total_sales || 0)}
                    </TableCell>
                    <TableCell className="text-center">-</TableCell>
                    <TableCell className="text-center">-</TableCell>
                    <TableCell className="text-right text-green-600">
                      {formatCurrency(metrics?.total_commission || 0)}
                    </TableCell>
                    <TableCell className="text-right text-amber-700">
                      {formatCurrency(metrics?.total_leftover || 0)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(metrics?.total_final_value ?? metrics?.total_commission ?? 0)}
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </If>
        </div>

        {/* Paginação */}
        {lastPage > 1 && (
          <Pagination
            current_page={currentPage}
            last_page={lastPage}
            handleNext={() => loadData(currentPage + 1)}
            handlePrevious={() => loadData(currentPage - 1)}
            handleLastPage={() => loadData(lastPage)}
          />
        )}
      </div>
    </Main>
  );
};
